/**
 * Asset screener listing dual-axis Highcharts fork (series-only).
 * Tooltip uses ListingDualAxisTooltip; keeps broadridge_chart.tsx untouched.
 */
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import ReactDOMServer from "react-dom/server";
import {
    ChartTypes,
    TimeSeriesPoint,
    TimeSeriesUnits,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_COLUMN_PLOT_OPTIONS,
    DEFAULT_TOOLTIP_PROPS,
} from "~/modules/visualization/consts";
import { DEFAULT_NAVIGATOR_CONTROLS } from "~/modules/visualization/components/Timeseries/consts";
import {
    ColumnPlotOptions,
    NavigatorControlState,
    YAxisConfig,
    YAxisTypes,
} from "~/modules/visualization/components/Timeseries/types";
import { formatXAxisTick } from "~/modules/visualization/components/functions/formatXAxisTick";
import { DEFAULT_TIMESERIES_LEGEND_PROPS } from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import {
    LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX,
    ListingDualAxisTooltip,
} from "~/modules/visualization/components/TimeSeriesChartV3/ListingDualAxisTooltip";
import { LegendProps } from "~/modules/visualization/components/TimeSeriesChartV3/types";
import { RWA_COLORS } from "~/styles/colors";
import { formatApy } from "~/utils/functions/formatApy";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import { hexToRGB } from "~/utils/hexToRGB";
import { WindowSizeState, useWindowSize } from "~/utils/useWindowSize";

// // // //

/** Matches `font-sans` / `--font-sans` (TWK Lausanne) for SVG text Highcharts does not inherit from Tailwind. */
const LISTING_DUAL_AXIS_CHART_FONT_FAMILY =
    '"Lausanne", ui-sans-serif, system-ui, sans-serif';

/** Slightly smaller / darker axis labels than shared visualization defaults (listing card chart only). */
const LISTING_DUAL_AXIS_AXIS_LABELS = {
    ...DEFAULT_AXIS_LABEL_PROPS,
    style: {
        ...DEFAULT_AXIS_LABEL_PROPS.style,
        fontFamily: LISTING_DUAL_AXIS_CHART_FONT_FAMILY,
        fontSize: "0.8125rem",
        color: RWA_COLORS.slate["500"],
    },
};

const LISTING_DUAL_AXIS_AXIS_TITLE_STYLE = {
    fontFamily: LISTING_DUAL_AXIS_CHART_FONT_FAMILY,
    fontSize: "0.8125rem",
    color: RWA_COLORS.slate["500"],
};

// New types for dual-axis support
interface DualAxisConfig {
    primary: YAxisConfig & {
        title?: string;
        unit?: TimeSeriesUnits;
        format?: string;
    };
    secondary: YAxisConfig & {
        title?: string;
        unit?: TimeSeriesUnits;
        format?: string;
        opposite?: boolean;
    };
}

interface DualAxisTimeSeriesView extends TimeSeriesView {
    yAxis?: 0 | 1; // 0 for primary axis, 1 for secondary axis
}

/**
 * Finds the `TimeSeriesView` with the most entries in the `item.points` array.
 * @param {TimeSeriesView[]} series - Array of `TimeSeriesView` objects.
 * @returns {TimeSeriesView | undefined} The `TimeSeriesView` with the most points or `undefined` if the array is empty.
 */
function findLongestSeries(
    series: TimeSeriesView[],
): TimeSeriesView | undefined {
    if (series.length === 0) {
        return undefined;
    }

    let maxView = series[0]; // Start with the first view as the maximum
    let maxPointsLength = maxView.item.points.length; // Length of points in the first view

    for (let i = 1; i < series.length; i++) {
        const currentView = series[i];
        const currentPointsLength = currentView.item.points.length;
        if (currentPointsLength > maxPointsLength) {
            maxView = currentView;
            maxPointsLength = currentPointsLength;
        }
    }

    return maxView;
}

// // // //

function sortSeries(metrics: TimeSeriesView[]): TimeSeriesView[] {
    return metrics.sort((a, b) => {
        const lastValueA = a.item.points[a.item.points.length - 1][1];
        const lastValueB = b.item.points[b.item.points.length - 1][1];

        return lastValueB - lastValueA;
    });
}

// // // //

function formatAxisValue(
    value: number,
    unit: TimeSeriesUnits,
    customFormat?: string,
    stacked: boolean = false,
    currencyValueDecimals: number = 2,
): string {
    if (customFormat) {
        return customFormat.replace(
            "{value}",
            value.toLocaleString("en-US", { maximumFractionDigits: 0 }),
        );
    }

    // Support stacked percent layout
    if (stacked) {
        return `${value}%`;
    }

    // Return dollars format
    if (unit === TimeSeriesUnits.dollar || unit === TimeSeriesUnits.dollars) {
        return `$${formatCurrencyValueCompact(value, currencyValueDecimals)}`;
    }

    // Return percent format
    if (unit === TimeSeriesUnits.percent) {
        return `${formatApy(value).toString()}%`;
    }

    // Return tokens format
    if (unit === TimeSeriesUnits.token) {
        return formatCurrencyValueCompact(value);
    }

    // Return years format
    if (unit === TimeSeriesUnits.year) {
        return `${value} years`;
    }

    // Return address format
    if (unit === TimeSeriesUnits.address) {
        return `${value.toLocaleString()}`;
    }

    // Ensure numeric before invoking toLocaleString
    if (typeof value === "number") {
        return value.toLocaleString();
    }

    return String(value);
}

function getChartOptions(props: {
    metrics: DualAxisTimeSeriesView[];
    windowSize: WindowSizeState;
    area: boolean;
    stacked: boolean;
    enableZoom: boolean;
    navigator: NavigatorControlState;
    height: number;
    legend: LegendProps;
    yAxis: YAxisConfig | DualAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    isDualAxis?: boolean;
    currencyValueDecimals?: number;
}) {
    const {
        metrics,
        windowSize,
        enableZoom = false,
        area,
        stacked,
        navigator,
        height,
        legend,
        yAxis,
        columnPlotOptions,
        isDualAxis = false,
        currencyValueDecimals = 2,
    } = props;

    // Determine units (default to dollars just to be safe incase metrics[0] is empty)
    const defaultUnit: TimeSeriesUnits = metrics[0]
        ? metrics[0].item.measure.unit
        : TimeSeriesUnits.dollar;

    // Hide legend on small screens
    let showLegend = legend.enabled || false;
    if (windowSize.isSm) {
        showLegend = false;
    }

    const xAxisMin = navigator.xAxis ? navigator.xAxis.min : undefined;
    const xAxisMax = navigator.xAxis ? navigator.xAxis.max : undefined;

    const chart: any = {
        height,
        // Flush with the card's section grid — no default 10px side gutters
        spacingLeft: 0,
        spacingRight: 0,
        backgroundColor: "transparent",
        animation: false, // Disable animation
        style: {
            fontFamily: LISTING_DUAL_AXIS_CHART_FONT_FAMILY,
        },
        zooming: {
            type: enableZoom ? "xy" : undefined,
            mouseWheel: {
                enabled: false, // Disable scroll-to-zoom
            },
        },
    };

    if (area && !isDualAxis) {
        chart.type = "area";
    }

    // Define navigatorConfig for Highcharts
    const navigatorConfig: {
        enabled?: boolean;
        series?: undefined | any;
    } = {
        enabled: navigator.active,
    };

    // If navigator is enabled, select the longest series to function as the basline for the navigator display
    if (navigator.enabled && navigator.active) {
        // Find longest series
        const longestSeries = findLongestSeries(metrics);

        // Use series for navigator display if available
        if (longestSeries) {
            navigatorConfig.series = {
                type: "area",
                color: RWA_COLORS.blue[500],
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1,
                    },
                    stops: [
                        [0, hexToRGB(RWA_COLORS.blue[500], 0.8)],
                        [1, hexToRGB(RWA_COLORS.blue[500], 0.2)],
                    ],
                },
                data: longestSeries.item.points.map((v: TimeSeriesPoint) => {
                    return [new Date(v[0]).getTime(), Number(v[1])];
                }),
            };
        }
    }

    // Configure Y-Axis(es)
    let yAxisConfig: any;

    if (isDualAxis && "primary" in yAxis) {
        const dualAxis = yAxis as DualAxisConfig;
        const zeroPlotLine = {
            value: 0,
            color: RWA_COLORS.slate["300"],
            width: 1,
            zIndex: 5,
            dashStyle: "Dash" as const,
        };

        yAxisConfig = [
            {
                // Primary Y-Axis
                min: dualAxis.primary.min,
                type: dualAxis.primary.type,
                ...DEFAULT_AXIS_PROPS,
                plotLines: [zeroPlotLine],
                title: {
                    text: dualAxis.primary.title ?? "Primary Axis",
                    style: LISTING_DUAL_AXIS_AXIS_TITLE_STYLE,
                },
                labels: {
                    ...LISTING_DUAL_AXIS_AXIS_LABELS,
                    enabled:
                        dualAxis.primary.enabled !== undefined
                            ? dualAxis.primary.enabled
                            : true,
                    formatter: function (): string {
                        // @ts-ignore
                        const value = this.value;
                        return formatAxisValue(
                            value,
                            dualAxis.primary.unit || defaultUnit,
                            dualAxis.primary.format,
                            stacked,
                            currencyValueDecimals,
                        );
                    },
                },
            },
            {
                // Secondary Y-Axis
                min: dualAxis.secondary.min,
                type: dualAxis.secondary.type,
                ...DEFAULT_AXIS_PROPS,
                plotLines: [zeroPlotLine],
                title: {
                    text: dualAxis.secondary.title ?? "Secondary Axis",
                    style: LISTING_DUAL_AXIS_AXIS_TITLE_STYLE,
                },
                opposite:
                    dualAxis.secondary.opposite !== undefined
                        ? dualAxis.secondary.opposite
                        : true,
                labels: {
                    ...LISTING_DUAL_AXIS_AXIS_LABELS,
                    enabled:
                        dualAxis.secondary.enabled !== undefined
                            ? dualAxis.secondary.enabled
                            : true,
                    formatter: function (): string {
                        // @ts-ignore
                        const value = this.value;
                        return formatAxisValue(
                            value,
                            dualAxis.secondary.unit || defaultUnit,
                            dualAxis.secondary.format,
                            stacked,
                        );
                    },
                },
            },
        ];
    } else {
        // Single Y-Axis (original implementation)
        const singleAxis = yAxis as YAxisConfig;
        yAxisConfig = {
            min: singleAxis.min,
            type: singleAxis.type,
            ...DEFAULT_AXIS_PROPS,
            labels: {
                ...LISTING_DUAL_AXIS_AXIS_LABELS,
                enabled:
                    singleAxis.enabled !== undefined
                        ? singleAxis.enabled
                        : true,
                formatter: function (): string {
                    // @ts-ignore
                    const value = this.value;
                    return formatAxisValue(
                        value,
                        defaultUnit,
                        undefined,
                        stacked,
                        currencyValueDecimals,
                    );
                },
            },
        };
    }

    const opts = {
        title: null,
        chart,
        tooltip: {
            useHTML: true,
            shared: true,
            outside: false,
            followPointer: true,
            animation: false,
            backgroundColor: "transparent",
            padding: 0,
            /** Stable width for the HTML tooltip layout (ListingDualAxisTooltip). */
            width: LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX,
            style: {
                fontFamily: LISTING_DUAL_AXIS_CHART_FONT_FAMILY,
                zIndex: 225,
            },
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const point = this.point;
                const xAxisDate = new Date(point.category);
                const xAxisLabel = formatXAxisTick(xAxisDate);
                const rows = (this.points || [])
                    .map((p: any) => {
                        const unit =
                            p.series?.userOptions?.custom?.unit ?? defaultUnit;
                        return {
                            label: p.series?.name ?? "",
                            color: p.color ?? p.series?.color ?? null,
                            value: typeof p.y === "number" ? p.y : null,
                            unit,
                        };
                    })
                    .filter((row: any) => row.label);

                return ReactDOMServer.renderToString(
                    <ListingDualAxisTooltip
                        dateLabel={xAxisLabel}
                        rows={rows}
                    />,
                );
            },
        },
        yAxis: yAxisConfig,
        xAxis: {
            type: "datetime",
            ...DEFAULT_AXIS_PROPS,
            min: xAxisMin,
            max: xAxisMax,
            crosshair: true,
            ordinal: true, // Exclude x-axis values where there's no data present
            // startOnTick: true, // Force axis to start on a tick
            // endOnTick: true, // Force axis to end on a tick
            labels: {
                ...LISTING_DUAL_AXIS_AXIS_LABELS,
                formatter: function (): string {
                    // @ts-ignore
                    const value = this.value;
                    return formatXAxisTick(new Date(value));
                },
            },
        },
        plotOptions: {
            series: {
                type: "spline",
                animation: {
                    duration: 0,
                },
                states: {
                    hover: {
                        lineWidthPlus: 0,
                    },
                },
                events: {
                    legendItemClick: function () {
                        return true;
                    },
                },
            },
        },
        credits: {
            enabled: false,
        },
        legend: {
            ...legend,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            enabled: showLegend,
            itemStyle: {
                ...(legend as { itemStyle?: Highcharts.CSSObject }).itemStyle,
                fontFamily: LISTING_DUAL_AXIS_CHART_FONT_FAMILY,
            },
        },
        navigator: navigatorConfig,
        rangeSelector: {
            enabled: false,
        },
        series: [
            ...metrics
                .filter((m) => m.enabled)
                .map((m) => {
                    const color = m.item.group.color || "#999999";

                    const seriesConfig: any = {
                        name: `${m.item.group.name}`,
                        custom: {
                            unit: m.item.measure.unit,
                        },
                        tooltip: {
                            valueDecimals: 2,
                        },
                        type: m.chartType,
                        // Make line / area charts thicker (2px), keep columns at default
                        lineWidth:
                            m.chartType === ChartTypes.line ||
                            m.chartType === ChartTypes.area
                                ? 2
                                : 1,
                        softThreshold:
                            m.chartType === ChartTypes.area ? false : undefined,
                        color:
                            m.chartType === ChartTypes.column
                                ? hexToRGB(color, 0.6)
                                : color,
                        data: m.item.points.map((v: TimeSeriesPoint) => {
                            if (m.invertYAxis) {
                                return [
                                    new Date(v[0]).getTime(),
                                    Number(v[1]) * -1,
                                ];
                            }
                            return [new Date(v[0]).getTime(), Number(v[1])];
                        }),
                        fillColor:
                            m.chartType === ChartTypes.area && isDualAxis
                                ? {
                                      linearGradient: {
                                          x1: 0,
                                          y1: 0,
                                          x2: 0,
                                          y2: 1,
                                      },
                                      stops: [
                                          [0, hexToRGB(color, 0.2)],
                                          [1, hexToRGB(color, 0)],
                                      ],
                                  }
                                : area && !isDualAxis
                                  ? {
                                        linearGradient: {
                                            x1: 0,
                                            y1: 0,
                                            x2: 0,
                                            y2: 1,
                                        },
                                        stops: [
                                            [0, hexToRGB(color, 0.8)],
                                            [1, hexToRGB(color, 0.2)],
                                        ],
                                    }
                                  : undefined,
                    };

                    // Add yAxis specification for dual-axis charts
                    if (isDualAxis && m.yAxis !== undefined) {
                        seriesConfig.yAxis = m.yAxis;
                    }

                    return seriesConfig;
                }),
        ],
    };

    // // // //

    if (area && !isDualAxis) {
        // @ts-ignore
        opts.plotOptions.area = {
            stacking: stacked ? "percent" : "overlap",
            marker: {
                enabled: false,
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        };
    }

    if (isDualAxis && metrics.some((m) => m.chartType === ChartTypes.area)) {
        // @ts-ignore
        opts.plotOptions.area = {
            stacking: "overlap",
            marker: {
                enabled: false,
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        };
    }

    // Handle column plot options - ensure side-by-side display
    if (
        columnPlotOptions ||
        metrics.some((m) => m.chartType === ChartTypes.column)
    ) {
        let columnPlotOptionsValue: ColumnPlotOptions = {
            ...DEFAULT_COLUMN_PLOT_OPTIONS,
        };

        if (columnPlotOptions) {
            columnPlotOptionsValue = {
                ...columnPlotOptionsValue,
                ...columnPlotOptions,
            };
        }

        // @ts-ignore
        opts.plotOptions.column = {
            ...columnPlotOptionsValue,
            // Ensure side-by-side display with tight grouping
            stacking: null,
            grouping: true,
            pointPadding: 0.02, // Very small padding - columns almost touching
            groupPadding: 0.15, // Slightly larger gap between time periods
        };
    }

    return opts;
}

interface Props {
    series: DualAxisTimeSeriesView[];
    area?: boolean;
    stacked?: boolean;
    legend?: LegendProps;
    enableZoom?: boolean;
    height?: number;
    navigator?: NavigatorControlState;
    yAxis?: YAxisConfig | DualAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    isDualAxis?: boolean;
    /** Decimal places for dollar values below $1K (e.g. per-share NAV). */
    currencyValueDecimals?: number;
}

/**
 * Dual-axis time series chart for asset screener expanded row (custom listing tooltip).
 */
function AssetScreenerListingDualAxisChartLayout(props: Props) {
    const {
        series,
        area = false,
        stacked = false,
        enableZoom = false,
        navigator = DEFAULT_NAVIGATOR_CONTROLS,
        legend = { ...DEFAULT_TIMESERIES_LEGEND_PROPS },
        height = 300,
        columnPlotOptions,
        isDualAxis = false,
        currencyValueDecimals = 2,
        yAxis = {
            min: 0,
            enabled: true,
            type: YAxisTypes.linear,
        },
    } = props;

    const chartSeries = sortSeries(series) as DualAxisTimeSeriesView[];

    const windowSize = useWindowSize();

    if (
        chartSeries.length === 0 ||
        (chartSeries.length === 1 && chartSeries[0].item.points.length < 2)
    ) {
        return (
            <div
                style={{ height: `${height}px` }}
                className="flex flex-col items-center justify-center gap-3 text-muted-foreground bg-card"
            >
                <FontAwesomeIcon icon={faChartLine} className="h-8 w-8" />
                <p className="text-sm font-light">
                    No data available for this selection
                </p>
            </div>
        );
    }

    const options = getChartOptions({
        metrics: chartSeries,
        windowSize,
        area,
        stacked,
        enableZoom,
        navigator,
        height,
        legend,
        columnPlotOptions,
        yAxis,
        isDualAxis,
        currencyValueDecimals,
    });

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export const AssetScreenerListingDualAxisChart = React.memo(
    AssetScreenerListingDualAxisChartLayout,
);
